import { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Download } from "lucide-react";
import { Chips, Empty, Stat, downloadCsv, money } from "./ui";

export const PAID = ["paid", "ready", "collected"];
export const PAY_METHODS = [["all", "All methods"], ["paypal", "PayPal"], ["card", "Card"], ["card_in_store", "Card in store"], ["cash", "Cash"], ["bank_transfer", "Bank transfer"], ["in_store", "Pay in store"], ["other", "Other"], ["none", "Not set"]];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const saleDay = (o) => ymd(new Date(o.paid_at || o.created_at));
const parse = (s) => new Date(`${s}T00:00:00`);
const methodName = (m) => (PAY_METHODS.find(([k]) => k === m) || ["", m || "—"])[1];

const unitFor = (from, to) => {
  const days = (parse(to) - parse(from)) / 864e5 + 1;
  return days <= 31 ? "day" : days <= 120 ? "week" : "month";
};
const keyOf = (s, unit) => {
  if (unit === "day") return s;
  if (unit === "month") return s.slice(0, 7);
  const d = parse(s);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return ymd(d);
};
const labelOf = (k, unit) => (unit === "month" ? `${MON[Number(k.slice(5, 7)) - 1]} ${k.slice(2, 4)}` : `${unit === "week" ? "Wk " : ""}${MON[Number(k.slice(5, 7)) - 1]} ${Number(k.slice(8))}`);

const CHART_BOX = { width: "100%", height: 260 };
const CHART_INIT = { width: 320, height: 260 };
const CHART_MARGIN = { top: 8, right: 4, left: -12, bottom: 0 };
const TICK = { fontSize: 11 };
const CURSOR = { fill: "rgba(201,162,39,.08)" };
const BAR_RADIUS = [3, 3, 0, 0];
const VIEW_OPTIONS = [["chart", "Graph"], ["table", "Table"]];
const dollars = (v) => `$${v}`;
const tipFormat = (v, n) => (n === "sales" ? [money(v), "Sales"] : [v, "Orders"]);

const buckets = (orders, from, to) => {
  const unit = unitFor(from, to);
  const map = new Map();
  for (let d = parse(from); d <= parse(to); d.setDate(d.getDate() + 1)) {
    const k = keyOf(ymd(d), unit);
    if (!map.has(k)) map.set(k, { label: labelOf(k, unit), sales: 0, orders: 0 });
  }
  orders.filter((o) => PAID.includes(o.status)).forEach((o) => {
    const b = map.get(keyOf(saleDay(o), unit));
    if (b) { b.sales = Math.round((b.sales + o.total) * 100) / 100; b.orders += 1; }
  });
  return { unit, rows: [...map.values()] };
};

export const SalesTotals = ({ orders }) => {
  const paid = orders.filter((o) => PAID.includes(o.status));
  const revenue = paid.reduce((n, o) => n + o.total, 0);
  const unpaid = orders.filter((o) => ["reserved", "pending_payment"].includes(o.status)).reduce((n, o) => n + o.total, 0);
  return (
    <div className="ad-stats" data-testid="orders-summary">
      <Stat label="Sales (paid)" value={money(revenue)} testId="orders-stat-revenue" />
      <Stat label="Paid orders" value={paid.length} hint={`${orders.length} total`} testId="orders-stat-paid" />
      <Stat label="Average order" value={money(paid.length ? revenue / paid.length : 0)} testId="orders-stat-average" />
      <Stat label="Items sold" value={paid.reduce((n, o) => n + o.items.reduce((m, i) => m + i.qty, 0), 0)} testId="orders-stat-items" />
      <Stat label="Reserved / unpaid" value={money(unpaid)} testId="orders-stat-reserved" />
    </div>
  );
};

export const SalesChart = ({ orders, from, to, title = "Paid sales" }) => {
  const { unit, rows } = useMemo(() => buckets(orders, from, to), [orders, from, to]);
  return (
    <div className="ad-card" data-testid="sales-chart">
      <h3>{title} by {unit}</h3>
      <div style={CHART_BOX}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0} initialDimension={CHART_INIT}>
          <BarChart data={rows} margin={CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
            <XAxis dataKey="label" tick={TICK} interval="preserveStartEnd" minTickGap={8} />
            <YAxis tick={TICK} tickFormatter={dollars} />
            <Tooltip formatter={tipFormat} cursor={CURSOR} />
            <Bar dataKey="sales" fill="#c9a227" radius={BAR_RADIUS} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const csvRows = (orders) => orders.map((o) => ({ Date: saleDay(o), Order: o.number, Client: o.name, Email: o.email, Phone: o.phone, Status: o.status,
  "Paid by": methodName(o.payment_method), Items: o.items.map((i) => `${i.qty}x ${i.brand} ${i.name}`).join("; "), Total: o.total.toFixed(2), "Paid at": o.paid_at }));

export const SalesTable = ({ orders, from, to }) => (
  <div className="ad-card" data-testid="sales-table">
    <div className="sales-table-head">
      <h3>Sales {from} → {to}</h3>
      <button className="btn light" disabled={!orders.length} onClick={() => downloadCsv(`wax-moi-shop-sales_${from}_${to}.csv`, csvRows(orders))} data-testid="orders-export-button"><Download size={15} /> Export CSV</button>
    </div>
    {!orders.length ? <Empty>No sales in this range.</Empty> : (
      <div className="sales-table-wrap">
        <table className="sales-table">
          <thead><tr><th>Date</th><th>Order</th><th>Client</th><th>Paid by</th><th>Status</th><th className="num">Total</th></tr></thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} data-testid={`sales-row-${o.id}`}>
                <td>{saleDay(o)}</td><td>{o.number}</td><td>{o.name}</td><td>{methodName(o.payment_method)}</td>
                <td><span className={`pill st-pill-${o.status}`}>{o.status.replace("_", " ")}</span></td><td className="num">{money(o.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr><td colSpan={5}>Paid total</td><td className="num" data-testid="sales-table-total">{money(orders.filter((o) => PAID.includes(o.status)).reduce((n, o) => n + o.total, 0))}</td></tr></tfoot>
        </table>
      </div>
    )}
  </div>
);

export const ReportFilters = ({ from, to, setFrom, setTo, method, setMethod, view, setView }) => (
  <div className="sales-filters" data-testid="sales-filters">
    <label className="mu-field">From<input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} data-testid="sales-from-input" /></label>
    <label className="mu-field">To<input type="date" value={to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)} data-testid="sales-to-input" /></label>
    <label className="mu-field">Payment method
      <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="sales-method-select">
        {PAY_METHODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
    </label>
    <Chips options={VIEW_OPTIONS} value={view} onChange={setView} testPrefix="sales-view" />
  </div>
);
